import React from "react";
import {
    Box,
    CircularProgress,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Popover,
    Typography,
    useTheme,
} from "@mui/material";
import { DeleteIcon } from "@/Components/Common/CustomIcons";
import { formatDateTime } from "@/lib/utils";
import { PanelInboxNotification } from "@/types/panelNotifications";

interface NotificationInboxPanelProps {
    anchorEl: HTMLElement | null;
    open: boolean;
    onClose: () => void;
    items: PanelInboxNotification[];
    loading: boolean;
    loadingMore: boolean;
    hasMore: boolean;
    onLoadMore: () => void;
    onSelect: (item: PanelInboxNotification) => void;
    onDelete: (id: string) => void;
}

const NotificationInboxPanel: React.FC<NotificationInboxPanelProps> = ({
    anchorEl,
    open,
    onClose,
    items,
    loading,
    loadingMore,
    hasMore,
    onLoadMore,
    onSelect,
    onDelete,
}) => {
    const theme = useTheme();

    const handleListScroll = (event: React.UIEvent<HTMLUListElement>) => {
        const listboxNode = event.currentTarget;
        if (
            hasMore &&
            !loadingMore &&
            listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 5
        ) {
            onLoadMore();
        }
    };

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            slotProps={{
                paper: {
                    elevation: 0,
                    sx: {
                        mt: 1.5,
                        width: 380,
                        maxWidth: "calc(100vw - 24px)",
                        maxHeight: 420,
                        borderRadius: "12px",
                        border: "1px solid",
                        borderColor: theme.palette.divider,
                        overflow: "hidden",
                    },
                },
            }}
        >
            <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid", borderColor: theme.palette.divider }}>
                <Typography fontSize={16} fontWeight={700}>
                    Notifications
                </Typography>
            </Box>
            {loading && items.length === 0 ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress size={28} />
                </Box>
            ) : items.length === 0 ? (
                <Box sx={{ py: 4, px: 2, textAlign: "center" }}>
                    <Typography fontSize={14} color="text.secondary">
                        No notifications yet
                    </Typography>
                </Box>
            ) : (
                <List
                    disablePadding
                    sx={{ overflowY: "auto", maxHeight: 340 }}
                    onScroll={handleListScroll}
                >
                    {items.map((item) => (
                        <ListItem
                            key={item._id}
                            disablePadding
                            secondaryAction={
                                <Box
                                    display="flex"
                                    alignItems="flex-start"
                                    pt={1.25}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <DeleteIcon
                                        sx={{ color: theme.palette.error.main, cursor: "pointer" }}
                                        onClick={() => onDelete(item._id)}
                                    />
                                </Box>
                            }
                        >
                            <ListItemButton
                                onClick={() => onSelect(item)}
                                sx={{ alignItems: "flex-start", py: 1.25, pr: 6 }}
                            >
                                <ListItemText
                                    primary={
                                        <Typography
                                            component="span"
                                            variant="body1"
                                            sx={{ display: "block", mb: 0.5, fontWeight: 600 }}
                                        >
                                            {item.title}
                                        </Typography>
                                    }
                                    secondary={
                                        <>
                                            <Typography
                                                component="span"
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ display: "block", mb: 0.5 }}
                                            >
                                                {item.body}
                                            </Typography>
                                            <Typography component="span" variant="caption" color="text.disabled">
                                                {item.sentAt ? formatDateTime(item.sentAt) : ""}
                                            </Typography>
                                        </>
                                    }
                                />
                            </ListItemButton>
                        </ListItem>
                    ))}
                    {loadingMore && (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 1.5 }}>
                            <CircularProgress size={22} />
                        </Box>
                    )}
                </List>
            )}
        </Popover>
    );
};

export default NotificationInboxPanel;
